import { useState, useEffect, useCallback } from 'react';
import {
  GET_VIDEO_SHARE_SETTING,
  UPDATE_VIDEO_SHARE_SETTING,
  GET_VIDEO_PERMISSIONS
} from '../api/videoShareSettingQueries';

export type VideoShareSettings = {
  videoShareSettingId: string;
  videoId: string;
  shareStatus: 'PUBLIC' | 'PRIVATE';
  accessLevel: 'GUEST' | 'GUEST_COMMENTER';
  expiryDate: string | null;
  createdByUserId: string | null;
  organisationId: string;
  createdAt: string;
  updatedAt: string;
};

export type UpdateInput = {
  shareStatus: 'PUBLIC' | 'PRIVATE';
  accessLevel?: 'GUEST' | 'GUEST_COMMENTER';
  expiryDate?: string | null;
};

export function useVideoShareSettings(apiClient: any, videoId: string) {
  const [settings, setSettings] = useState<VideoShareSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);
  const [canEdit, setCanEdit] = useState(true);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const [shareResult, permResult] = await Promise.all([
        apiClient.query({
          query: GET_VIDEO_SHARE_SETTING,
          variables: { videoId },
          fetchPolicy: 'network-only',
          context: { module: 'video' }
        }),
        apiClient.query({
          query: GET_VIDEO_PERMISSIONS,
          variables: { videoId },
          fetchPolicy: 'network-only',
          context: { module: 'video' }
        })
      ]);
      setSettings(shareResult.data?.getVideoShareSetting || null);
      const permissions = permResult.data?.getVideo?.permissions || [];
      setCanEdit(permissions.includes('UPDATE'));
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [apiClient, videoId]);

  useEffect(() => {
    if (videoId) {
      fetchSettings();
    }
  }, [fetchSettings, videoId]);

  const updateSettings = useCallback(
    async (input: UpdateInput) => {
      try {
        const { data } = await apiClient.mutate({
          mutation: UPDATE_VIDEO_SHARE_SETTING,
          variables: { videoId, input },
          context: { module: 'video' }
        });
        setSettings(data?.updateVideoShareSetting || null);
        return data?.updateVideoShareSetting;
      } catch (err) {
        setError(err);
        throw err;
      }
    },
    [apiClient, videoId]
  );

  return { settings, loading, error, updateSettings, refetch: fetchSettings, canEdit };
}
